/**
 * Header mega-menu behavior — reference hover/pin pattern.
 *
 * Open:  mouseenter on .header__megamenu-item
 *          → .mega-menu-open on .header-wrapper-new
 *          → .active on .header-mega-menu__overlay
 *          → data-state="open" on matching .header-mega-menu panel
 *          → aria-expanded="true" on trigger
 *
 * Close: mouseleave on the trigger's parent <li>
 *          → removes all of the above
 *
 * Pin:   click on href="#" trigger
 *          → disableHover=true + activeItem set (reference pattern)
 *          → outside-click or escape unpins
 *
 * Third-level switch: mouseenter on .header-mega-menu__have-submenu__list .first-link
 *          → .show-sub-links on hovered item; cleared from siblings
 *
 * ponytail: jQuery available globally (loaded non-defer before this bundle);
 *           all state is DOM attrs/classes — no shared store.
 */

/**
 * @returns {{ destroy(): void }}
 */
export default function initHeaderMegaMenu() {
  const root = document.querySelector('.header-wrapper-new');
  if (!root) return { destroy() {} };

  const overlay = root.querySelector('.header-mega-menu__overlay');

  /** Listeners registry for destroy() */
  const listeners = [];

  function on(el, type, fn, opts) {
    el.addEventListener(type, fn, opts);
    listeners.push({ el, type, fn, opts });
  }

  // ── State ──────────────────────────────────────────────────────────────────
  let disableHover = false;
  let resizeObserver = null;
  let activeItem = null; // the pinned trigger anchor

  // ── Helpers ────────────────────────────────────────────────────────────────

  function getPanel(handle) {
    return root.querySelector(`.header-mega-menu[data-megamenu-panel="${handle}"]`);
  }

  function panelFocusables(panel) {
    return [...panel.querySelectorAll('a[href], button:not([disabled]), [tabindex="0"]')].filter(
      (el) => el.offsetParent !== null && !el.hasAttribute('hidden'),
    );
  }

  function openMenu(trigger) {
    const handle = trigger.dataset.megamenuTrigger;
    if (!handle) return;

    root.classList.add('mega-menu-open');
    if (overlay) overlay.classList.add('active');

    // Close other panels first
    root.querySelectorAll('.header-mega-menu[data-state="open"]').forEach((p) => {
      p.setAttribute('data-state', 'closed');
    });
    root.querySelectorAll('.header__megamenu-item[aria-expanded="true"]').forEach((t) => {
      t.setAttribute('aria-expanded', 'false');
      t.setAttribute('data-state', 'closed');
    });

    const panel = getPanel(handle);
    if (panel) panel.setAttribute('data-state', 'open');
    trigger.setAttribute('aria-expanded', 'true');
    trigger.setAttribute('data-state', 'open');
  }

  function closeMenu() {
    root.classList.remove('mega-menu-open');
    if (overlay) overlay.classList.remove('active');

    root.querySelectorAll('.header-mega-menu').forEach((p) => {
      p.setAttribute('data-state', 'closed');
    });
    root.querySelectorAll('.header__megamenu-item').forEach((t) => {
      if (t.hasAttribute('aria-expanded')) {
        t.setAttribute('aria-expanded', 'false');
        t.setAttribute('data-state', 'closed');
      }
    });
  }

  function unpin() {
    disableHover = false;
    activeItem = null;
    closeMenu();
  }

  // ── Hover intent: delay close so the pointer can travel trigger → panel ────
  // (immediate close made the menu vanish before the user could move down)
  let closeTimer = null;

  function scheduleClose() {
    cancelClose();
    closeTimer = setTimeout(closeMenu, 300);
  }

  function cancelClose() {
    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }
  }

  // ── Bind triggers ──────────────────────────────────────────────────────────
  root.querySelectorAll('.header__megamenu-item').forEach((trigger) => {
    // Hover open
    on(trigger, 'mouseenter', () => {
      if (disableHover) return;
      cancelClose();
      openMenu(trigger);
    });

    // Hover close on parent <li> — guarded so moving into the panel stays open.
    // ponytail: panel lives outside <li> (split DOM), so we must check relatedTarget.
    const li = trigger.parentElement;
    if (li) {
      on(li, 'mouseleave', (e) => {
        if (disableHover) return;
        const dest = e.relatedTarget;
        // Null = mouse left window → close (delayed).
        if (!dest) { scheduleClose(); return; }
        // Moving into the matching panel → stay open.
        const handle = trigger.dataset.megamenuTrigger;
        const panel = handle && getPanel(handle);
        if (panel && panel.contains(dest)) return;
        // Moving into another trigger (switching panels) → let that trigger's mouseenter handle it.
        if (dest.closest('.header__megamenu-item')) return;
        scheduleClose();
      });
    }

    // Click-to-pin for href="#" items
    on(trigger, 'click', (e) => {
      const href = trigger.getAttribute('href');
      if (href !== '#') return;

      e.preventDefault();

      if (activeItem === trigger) {
        // Second click on same pinned item → unpin + close
        unpin();
      } else {
        disableHover = true;
        activeItem = trigger;
        openMenu(trigger);
      }
    });

    // Keyboard open — Enter/Space toggles the pinned panel (works on real-href
    // anchors too, where Enter would otherwise navigate) and moves focus in.
    // Tab while open bridges into the panel (panels sit far away in the DOM,
    // so natural tab order would skip them entirely).
    on(trigger, 'keydown', (e) => {
      const handle = trigger.dataset.megamenuTrigger;
      if (!handle) return;

      if (e.key === 'Tab' && activeItem === trigger && !e.shiftKey) {
        const panel = getPanel(handle);
        const first = panel && panelFocusables(panel)[0];
        if (first) {
          e.preventDefault();
          first.focus();
        }
        return;
      }

      if (e.key === 'Tab' && activeItem === trigger && e.shiftKey) {
        // leaving backwards — close so the menu doesn't linger open
        unpin();
        return;
      }

      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();

      if (activeItem === trigger) {
        unpin();
        return;
      }
      disableHover = true;
      activeItem = trigger;
      openMenu(trigger);
      const panel = getPanel(handle);
      const first = panel && panelFocusables(panel)[0];
      first?.focus();
    });
  });

  // ── Panel-side close — moving out of panel closes unless back on a trigger ──
  // ponytail: panels are outside the <li>, so panel needs its own mouseleave guard.
  root.querySelectorAll('.header-mega-menu').forEach((panel) => {
    // Entering the panel cancels any pending close from the trigger's mouseleave
    on(panel, 'mouseenter', cancelClose);

    on(panel, 'mouseleave', (e) => {
      if (disableHover) return;
      const dest = e.relatedTarget;
      // Null = left window → close (delayed).
      if (!dest) { scheduleClose(); return; }
      // Moving back to a trigger anchor → its mouseenter handles state; don't double-close.
      if (dest.closest('.header__megamenu-item')) return;
      // Moving into another panel (shouldn't normally happen, but guard it).
      if (dest.closest('.header-mega-menu')) return;
      scheduleClose();
    });
  });

  // ── Panel tab order — bridge back to the trigger and out to the next item ──
  // Panels live outside the nav in the DOM; without this, Shift+Tab from the
  // first panel link lands on whatever precedes the panel (the header icons).
  root.querySelectorAll('.header-mega-menu').forEach((panel) => {
    on(panel, 'keydown', (e) => {
      if (e.key !== 'Tab' || !activeItem) return;
      const focusables = panelFocusables(panel);
      if (!focusables.length) return;
      const active = document.activeElement;

      if (e.shiftKey && active === focusables[0]) {
        // Shift+Tab on the FIRST panel control → back to the trigger, panel stays open
        e.preventDefault();
        activeItem.focus();
        return;
      }

      if (!e.shiftKey && active === focusables[focusables.length - 1]) {
        e.preventDefault();

        // Clinics panel: after walking the selected location's card, Tab moves
        // on to the NEXT location row (if any) instead of leaving the panel.
        const selectedRow = panel.querySelector('[data-location-trigger][data-selected]');
        if (selectedRow) {
          const rows = [...panel.querySelectorAll('.header-mega-menu-locations__list-item')];
          const nextRow = rows[rows.indexOf(selectedRow.closest('.header-mega-menu-locations__list-item')) + 1];
          const target = nextRow && (nextRow.hasAttribute('data-location-trigger') ? nextRow : nextRow.querySelector('a'));
          if (target) {
            target.focus();
            return;
          }
        }

        // Tab on the LAST panel control → close and continue to the next nav item
        const trigger = activeItem;
        unpin();
        const nextLi = trigger.closest('li')?.nextElementSibling;
        // fallback: the VISIBLE search trigger (first match is the hidden mobile one)
        const visibleSearch = [...document.querySelectorAll('[data-header-search-trigger]')].find(
          (t) => t.offsetParent !== null,
        );
        const next = nextLi?.querySelector('a, button') || visibleSearch;
        next?.focus();
      }
    });

    // Focus leaving the panel by any other route (click elsewhere etc.) closes it
    on(panel, 'focusout', (e) => {
      const dest = e.relatedTarget;
      if (!dest) return; // window blur / click-away handled elsewhere
      if (panel.contains(dest)) return;
      if (dest.closest && dest.closest('.header__megamenu-item')) return; // back on a trigger
      unpin();
    });
  });

  // ── Outside-click unpins ──────────────────────────────────────────────────
  const onDocClick = (e) => {
    if (!disableHover) return;
    // Clicked inside the active trigger or its panel → stay open
    if (activeItem && activeItem.contains(e.target)) return;
    const handle = activeItem && activeItem.dataset.megamenuTrigger;
    const panel = handle && getPanel(handle);
    if (panel && panel.contains(e.target)) return;

    unpin();
  };
  document.addEventListener('click', onDocClick);

  // ── Escape unpins ─────────────────────────────────────────────────────────
  const onKeydown = (e) => {
    if (e.key !== 'Escape') return;
    if (!root.classList.contains('mega-menu-open')) return;
    const pinned = activeItem;
    unpin();
    pinned?.focus();
  };
  document.addEventListener('keydown', onKeydown);

  // ── Third-level column switch ─────────────────────────────────────────────
  // .first-link mouseenter → .show-sub-links on that item; clear siblings
  root.querySelectorAll('.header-mega-menu__have-submenu__list .first-link').forEach((item) => {
    const list = item.closest('.header-mega-menu__have-submenu__list');
    on(item, 'mouseenter', () => {
      if (!list) return;
      list.querySelectorAll('.first-link').forEach((sibling) => {
        sibling.classList.remove('show-sub-links', 'remove-sub-links');
      });
      item.classList.add('show-sub-links');
    });
  });

  // ── Scroll: sticky hide/reveal + overlay→solid flip ───────────────────────
  const SCROLL_THRESHOLD = 50; // hide/reveal trigger — small, independent of hero
  const isSticky = root.classList.contains('header--sticky');
  const isOverlay = root.classList.contains('header--overlay');
  let lastY = window.scrollY;
  let rafPending = false;

  // Glass→solid flip point = the hero's height, so the header stays glass while
  // it overlays the hero image and flips to solid white once scrolled past it.
  // Hero banners vary in height (image-driven), so measure the first content
  // section live and keep it fresh with a ResizeObserver (fires when the banner
  // image loads or the banner is swapped in the editor).
  const heroSection = document.querySelector('#MainContent')?.firstElementChild || null;
  let flipThreshold = SCROLL_THRESHOLD;
  function measureFlipThreshold() {
    if (!heroSection) return;
    const heroBottom = heroSection.offsetTop + heroSection.offsetHeight;
    const headerH = root.offsetHeight || 54;
    // Flip as the hero's bottom edge reaches the header's bottom edge.
    flipThreshold = Math.max(SCROLL_THRESHOLD, heroBottom - headerH);
  }
  if (isOverlay && heroSection) {
    measureFlipThreshold();
    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(measureFlipThreshold);
    resizeObserver.observe(heroSection);
    }
    window.addEventListener('resize', measureFlipThreshold, { passive: true });
  }

  function onScroll() {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(() => {
      rafPending = false;
      const y = window.scrollY;

      if (isSticky) {
        if (y > SCROLL_THRESHOLD && y > lastY) {
          root.classList.add('header--hidden');
        } else if (y < lastY) {
          root.classList.remove('header--hidden');
        }
      }

      if (isOverlay) {
        root.classList.toggle('header--scrolled', y > flipThreshold);
      }

      lastY = y;
    });
  }

  if (isSticky || isOverlay) {
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // ── Location selector (clinics panel — data-location-* swap) ─────────────
  // ponytail: scoped to root (.header-wrapper-new); no-ops gracefully if clinics absent.
  root.querySelectorAll('[data-location-trigger]').forEach((trigger) => {
    const selectLocation = () => {
      const handle = trigger.dataset.locationTrigger;
      if (!handle) return;

      root.querySelectorAll('[data-location-trigger]').forEach((t) => {
        const selected = t.dataset.locationTrigger === handle;
        if (selected) {
          t.dataset.selected = '';
        } else {
          delete t.dataset.selected;
        }
        t.setAttribute('aria-pressed', String(selected));
      });

      root.querySelectorAll('[data-location-detail]').forEach((el) => {
        if (el.dataset.locationDetail === handle) {
          el.dataset.selected = '';
        } else {
          delete el.dataset.selected;
        }
      });

      root.querySelectorAll('[data-location-offer]').forEach((el) => {
        if (el.dataset.locationOffer === handle) {
          el.dataset.selected = '';
        } else {
          delete el.dataset.selected;
        }
      });
    };

    // hover swaps the detail card; click kept for touch devices
    on(trigger, 'mouseenter', selectLocation);
    on(trigger, 'click', selectLocation);

    // role=button rows — Enter/Space activate like a real button, then move
    // focus into the swapped detail card (it holds the actionable links)
    on(trigger, 'keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      selectLocation();
      const detail = root.querySelector('[data-location-detail][data-selected]');
      detail?.querySelector('a[href], button')?.focus();
    });
  });

  // ── destroy() ─────────────────────────────────────────────────────────────
  function destroy() {
    listeners.forEach(({ el, type, fn, opts }) => el.removeEventListener(type, fn, opts));
    document.removeEventListener('click', onDocClick);
    document.removeEventListener('keydown', onKeydown);
    if (isSticky || isOverlay) {
      window.removeEventListener('scroll', onScroll);
    }
    resizeObserver?.disconnect();
    window.removeEventListener('resize', measureFlipThreshold);
    cancelClose();
    closeMenu();
    disableHover = false;
    activeItem = null;
  }

  return { destroy };
}
